import React, { useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import WBCModal from "../elements/WBCModal";
import Theme from "../../Theme";
import { WBCPrimaryButton } from "../elements/WBCPrimaryButton";

const ModalBody = styled.div`
  padding: 10px;
`;

const ModalLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
  color: ${Theme.wbcBlue};
`;

const ModalTextarea = styled.textarea`
  width: 100%;
  height: 120px;
  padding: 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  resize: vertical;
  font-family: inherit;
  font-size: 14px;
  direction: ltr;
  text-align: left;
  unicode-bidi: normal;

  &:focus {
    outline: none;
    border-color: ${Theme.wbcBlue};
    box-shadow: 0 0 0 2px rgba(30, 55, 109, 0.2);
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px;
  border-top: 1px solid #eee;
`;

const TeamCommentsModal = ({ isOpen, team, onClose, onSave }) => {
  const textareaRef = useRef(null);

  const handleSave = useCallback(() => {
    if (team && onSave && textareaRef.current) {
      onSave(team.id, textareaRef.current.value);
    }
    onClose();
  }, [team, onSave, onClose]);

  useEffect(() => {
    if (isOpen && textareaRef.current && team) {
      textareaRef.current.value = team.comments || "";
    }
  }, [isOpen, team]);

  const BodyContent = ({ children }) => <div>{children}</div>;
  BodyContent.displayName = "BodyContent";
  BodyContent.propTypes = {
    children: PropTypes.node
  };

  const FooterContent = ({ children }) => <div>{children}</div>;
  FooterContent.displayName = "FooterContent";
  FooterContent.propTypes = {
    children: PropTypes.node
  };

  if (!team) return null;

  return (
    <WBCModal
      isOpen={isOpen}
      toggleModal={() => onClose()}
      headerText={`Comments for ${team.fedTeamName || "Team"}`}
      onClose={onClose}
      size="md"
    >
      <BodyContent>
        <ModalBody>
          <ModalLabel htmlFor="teamComments">Enter comments for {team.fedTeamName}:</ModalLabel>
          <ModalTextarea
            ref={textareaRef}
            id="teamComments"
            placeholder="Enter comments for this team..."
            autoFocus
            dir="ltr"
            style={{ direction: "ltr", textAlign: "left" }}
          />
        </ModalBody>
      </BodyContent>

      <FooterContent>
        <ModalFooter>
          <WBCPrimaryButton width="auto" height="auto" onClick={handleSave}>
            Save Comments
          </WBCPrimaryButton>
          <WBCPrimaryButton onClick={onClose}>Cancel</WBCPrimaryButton>
        </ModalFooter>
      </FooterContent>
    </WBCModal>
  );
};

TeamCommentsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  team: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    fedTeamName: PropTypes.string,
    comments: PropTypes.string
  }),
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired
};

export default TeamCommentsModal;
